/**
 * ReviewSettingsPanel — 복습 설정 공용 패널 (ECHO 톤)
 * 단어테스트 · 모의고사에서 동일한 UI로 사용합니다.
 *
 * Props:
 *   isRetryEnabled     : boolean
 *   onToggleRetry      : () => void
 *   isAutoRetryEnabled : boolean
 *   onSetAutoRetry     : (bool) => void
 *   maxRetries         : number  (-1 = 무제한)
 *   onSetMaxRetries    : (number) => void
 */
const RETRY_OPTIONS = [1, 2, 3, 5, 10, -1];

const ReviewSettingsPanel = ({
  isRetryEnabled,
  onToggleRetry,
  isAutoRetryEnabled,
  onSetAutoRetry,
  maxRetries,
  onSetMaxRetries,
}) => {
  return (
    <div
      style={{
        border: "1px solid #E6EDEC",
        borderRadius: "14px",
        overflow: "hidden",
        backgroundColor: "#FFF",
      }}
    >
      {/* 헤더 + 토글 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "13px 16px",
          backgroundColor: "#F4F8F7",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "0.86rem",
              fontWeight: 800,
              color: "#0E141B",
              letterSpacing: "-0.01em",
            }}
          >
            복습
          </div>
          <div
            style={{ fontSize: "0.74rem", color: "#7A8490", marginTop: "1px" }}
          >
            틀린 문제만 모아 다시 풀게 합니다
          </div>
        </div>
        <EchoToggle checked={isRetryEnabled} onChange={onToggleRetry} />
      </div>

      {isRetryEnabled && (
        <div style={{ padding: "14px 16px" }}>
          {/* 플로우 안내 */}
          <div style={hintStyle}>
            <b style={mintB}>첫 풀이</b> → 틀린 문제만{" "}
            <b style={mintB}>복습 1회</b> → 또 틀리면{" "}
            <b style={mintB}>복습 2회</b> … 전부 맞히면 종료됩니다.
          </div>

          {/* 자동 / 수동 */}
          <div style={{ ...sectionLabel, marginTop: "14px" }}>복습 방식</div>
          <div style={{ display: "grid", gap: "6px" }}>
            <ModeCard
              selected={isAutoRetryEnabled}
              onClick={() => onSetAutoRetry(true)}
              title="자동 복습"
              desc="학생이 제출하면 틀린 문제가 즉시 복습 과제로 등록됩니다."
            />
            <ModeCard
              selected={!isAutoRetryEnabled}
              onClick={() => onSetAutoRetry(false)}
              title="수동 복습"
              desc="선생님이 복습 문제지 목록에서 직접 시점을 정해 출제합니다."
            />
          </div>

          {/* 최대 횟수 */}
          <div style={{ ...sectionLabel, marginTop: "16px" }}>
            최대 복습 횟수
          </div>
          <div style={{ display: "inline-flex", gap: "5px", flexWrap: "wrap" }}>
            {RETRY_OPTIONS.map((v) => (
              <button
                key={v}
                onClick={() => onSetMaxRetries(v)}
                style={{
                  padding: "6.5px 15px",
                  border: `1.5px solid ${maxRetries === v ? "#0E141B" : "#E6EDEC"}`,
                  borderRadius: "999px",
                  backgroundColor: maxRetries === v ? "#0E141B" : "#FFF",
                  color: maxRetries === v ? "#FFF" : "#3A434E",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  fontFamily: "inherit",
                  letterSpacing: "-0.01em",
                  cursor: "pointer",
                  transition: "all .12s",
                }}
              >
                {v === -1 ? "무제한" : `${v}회`}
              </button>
            ))}
          </div>
          <div
            style={{
              fontSize: "0.74rem",
              color: "#7A8490",
              marginTop: "8px",
              lineHeight: 1.5,
            }}
          >
            {maxRetries === -1
              ? "모든 문제를 맞힐 때까지 복습이 계속됩니다. 부담될 수 있어 3~5회를 권장해요."
              : `복습이 ${maxRetries}회 진행된 뒤에는 남은 오답이 있어도 종료됩니다.`}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── 하위 요소 ── */
const EchoToggle = ({ checked, onChange }) => (
  <div
    onClick={onChange}
    style={{
      width: "46px",
      height: "27px",
      borderRadius: "999px",
      backgroundColor: checked ? "#00D9C0" : "#D6DEE0",
      position: "relative",
      cursor: "pointer",
      transition: "background-color .15s",
      flexShrink: 0,
    }}
  >
    <span
      style={{
        position: "absolute",
        top: "3px",
        left: checked ? "22px" : "3px",
        width: "21px",
        height: "21px",
        borderRadius: "50%",
        backgroundColor: "#FFF",
        boxShadow: "0 1px 3px rgba(14,20,27,.18)",
        transition: "left .15s",
      }}
    />
  </div>
);

const ModeCard = ({ selected, onClick, title, desc }) => (
  <div
    onClick={onClick}
    style={{
      padding: "11px 13px",
      border: `1.5px solid ${selected ? "#00D9C0" : "#E6EDEC"}`,
      backgroundColor: selected ? "#F2FDFB" : "#FFF",
      borderRadius: "12px",
      cursor: "pointer",
      transition: "all .12s",
    }}
  >
    <div
      style={{
        fontSize: "0.84rem",
        fontWeight: 800,
        color: "#0E141B",
        display: "flex",
        alignItems: "center",
        gap: "7px",
      }}
    >
      <span
        style={{
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: selected ? "#00D9C0" : "#D6DEE0",
          display: "inline-block",
        }}
      />
      {title}
    </div>
    <div
      style={{
        fontSize: "0.74rem",
        color: "#7A8490",
        marginTop: "3px",
        lineHeight: 1.5,
        paddingLeft: "15px",
      }}
    >
      {desc}
    </div>
  </div>
);

const hintStyle = {
  padding: "10px 13px",
  backgroundColor: "#F4F8F7",
  borderRadius: "10px",
  fontSize: "0.78rem",
  color: "#3A434E",
  lineHeight: 1.55,
};
const mintB = { color: "#00A396", fontWeight: 800 };
const sectionLabel = {
  fontSize: "0.72rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  color: "#7A8490",
  marginBottom: "7px",
};

export default ReviewSettingsPanel;
